import { querySticker } from 'services/api/conversation';
import { queryStickerDataV2Cache, queryStickerEtagV2Cache } from 'services/cache';
import { useMemoizedFn, useSafeState } from 'ahooks';
import { Sticker, UserDB } from 'lib/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { toUpperCase } from 'utils/toLowerCase';

const removeOldCache = () => {
  localStorage.removeItem('@fett/querySticker/etag');
  localStorage.removeItem('@fett/querySticker/data');
  queryStickerDataV2Cache.remove();
  queryStickerEtagV2Cache.remove();
};

removeOldCache();

const STICKER_API_CACHE_KEY = 'querySticker';

export const useGetAllSticker = () => {
  return useMemoizedFn(async () => {
    try {
      const row = await UserDB.instance?.apiCacheTag
        .where('apiKey')
        .equals(STICKER_API_CACHE_KEY)
        .first();
      const { etag, stickerPack } = await querySticker({
        etag: row?.etag,
      });
      if (stickerPack?.length > 0 && UserDB.instance) {
        await UserDB.instance.transaction('rw', UserDB.instance.sticker, async () => {
          await UserDB.instance?.sticker.clear();
          await UserDB.instance?.sticker.bulkAdd(stickerPack, { allKeys: true });
        });
        await UserDB.instance?.apiCacheTag.put(
          {
            etag,
            apiKey: STICKER_API_CACHE_KEY,
          },
          STICKER_API_CACHE_KEY,
        );
      }
    } catch (err: any) {
      // eTag 不变 后端返回304 取前端缓存值
      if (err?.message?.includes('304')) {
        console.log('sticker cache 304');
      }
    }
  });
};

export type GroupingPoperties = keyof Pick<Conversation.Sticker, 'pid' | 'channel' | 'region'>;

export interface StikerListParams {
  channel: General.CaselessPlatformTunnel;
  region: General.CaselessRegion;
  filter: 'unset' | 'enabled';
  groupBy: GroupingPoperties;
}

export interface StickersParams extends Omit<StikerListParams, 'filter' | 'groupBy'> {
  filter?: StikerListParams['filter'];
}

export const useStickers = (params: StickersParams) => {
  const { filter = 'unset' } = params;
  const channel = toUpperCase(params.channel);
  const region = toUpperCase(params.region);

  const stickers = useLiveQuery(async () => {
    const data = await UserDB.instance?.sticker
      .where({
        channel,
        region: getStickerRegion(channel, region),
      })
      .toArray();
    return filter === 'enabled' ? data?.filter((s) => s.enable) : data;
  }, [channel, region, filter]);

  return stickers;
};

export interface StickerParams {
  channel: Sticker['channel'];
  region: Sticker['region'];
  sid?: Sticker['sid'];
  pid?: Sticker['pid'];
}

export const getStickerRegion = (
  channel: General.PlatformUpperCaseTunnel,
  region: StickerParams['region'],
) => {
  return channel === 'SHOPEE' ? region : 'GLOBAL';
};

export const useGetSticker = () => {
  return async (params: StickerParams) => {
    const { channel, region, sid, pid } = params;
    const query: StickerParams = {
      channel,
      region: getStickerRegion(channel, region),
    };
    if (sid) {
      query.sid = sid;
    }
    if (pid) {
      query.pid = pid;
    }
    return await UserDB.instance?.sticker.get(query);
  };
};

interface ShopeeStickerParams {
  region: StickerParams['region'];
  pid: StickerParams['pid'];
  sid: StickerParams['sid'];
}

// shopee 不同平台间有出现 pid 和 sid 一致但表情不一致的情况，所以不能只通过 pid 和 sid 寻找表情，需要加上 region 条件
export const useGetShopeeSticker = () => {
  return async (params: ShopeeStickerParams) => {
    const { region, pid, sid } = params;
    let sticker = await UserDB.instance?.sticker.get({
      channel: 'SHOPEE',
      region,
      pid,
      sid,
    });
    if (!sticker && region !== 'SG') {
      // FIX: 部分表情包在对应 region 无法查找到，在 SG 可以命中
      // 因此这里 fallback 到 SG 站点查询表情包
      sticker = await UserDB.instance?.sticker.get({
        channel: 'SHOPEE',
        region: 'SG',
        pid,
        sid,
      });
    }
    return sticker;
  };
};

export const useSticker = (params: StickerParams) => {
  const [loading, setLoading] = useSafeState(true);
  const { channel, region, sid, pid } = params;

  const getSticker = useGetSticker();
  const getShopeeSticker = useGetShopeeSticker();

  const sticker = useLiveQuery(async () => {
    setLoading(true);
    if (channel === 'SHOPEE') {
      return await getShopeeSticker({
        region,
        sid,
        pid,
      }).finally(() => {
        setLoading(false);
      });
    }
    if (channel === 'LAZADA' || channel === 'TOKOPEDIA') {
      return await getSticker({
        channel,
        region: 'GLOBAL',
        sid,
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [channel, region, sid, pid]);

  return { loading, sticker };
};
